import React, { useState } from 'react';
import { AccordionDetails, Typography, Box, Stack, Tooltip } from '@mui/material';
import { VrLessonProgressSection } from 'components/views/myTrainings/VrLessonProgressSection/VrLessonProgressSection';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CloudDownload } from '@mui/icons-material';
import { GameLessonProgress, LessonProgress, VrLessonProgress } from 'models/LessonProgress';
import { LessonType } from 'models/LessonType';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  StyledAccordion,
  StyledAccordionSummary,
  LessonNameBox,
  UnderlinedTypography,
  PercentageBox,
  StyledLinearProgress,
  PointsBox,
  HistoricalAccordion,
  HistoricalAccordionSummary,
  PointsTypography,
  ReportIconButton
} from './LessonProgressAccordion.styles';
import HistoricalProgresses from 'components/views/myTrainings/HistoricalProgresses/HistoricalProgresses';
import { GameLessonProgressSummary } from 'components/views/myTrainings/GameLessonProgressSummary/GameLessonProgressSummary';
import { truncateString } from 'helpers/stringHelper';
import { LessonProgressStatus } from "models/LessonProgressStatus";
import { useDownloadMyNodeReport } from '@hooks/node/useDownloadMyNodeReport';

interface LessonProgressAccordionProps {
    index: number;
    lp: LessonProgress;
    historicalProgresses: LessonProgress[];
    expanded: number | null;
    handleChange: (panel: number) => (event: React.SyntheticEvent) => void;
}

export const LessonProgressAccordion = (props: LessonProgressAccordionProps) => {
  const [hasMounted, setHasMounted] = useState(false);
  const { index, lp, expanded, handleChange } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const { downloadReport, isReportLoading } = useDownloadMyNodeReport();

  React.useEffect(() => {
    if (expanded === index) {
      setHasMounted(true);
    }
  }, [expanded, index]);

  const version = lp.lessonVersion;
  const maxPoints = version.maxPoints || 1;
  const getScoredPoints = () =>
    !version ? 0 : (lp.pointsScored / maxPoints * 100).toFixed(0);

  const isVr = lp.type === LessonType.VR;
  const isGame = lp.type === LessonType.GAME;

  return (
    <StyledAccordion key={index} expanded={!lp?.createdAt ? false : expanded === index} onChange={handleChange(index)}>
        <StyledAccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          expandIcon={<ExpandMoreIcon />}
        >
            <Stack direction={'row'} width={'100%'} display={'flex'} justifyContent={'space-between'} paddingLeft={theme.spacing(2)} alignItems={'center'}>
                <LessonNameBox>
                  <UnderlinedTypography>{truncateString(`${version.lessonName} (v${version.version})`, 25)}</UnderlinedTypography>
                </LessonNameBox>
                <PercentageBox sx={{ paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) }}>
                  <Typography>{getScoredPoints() + ' %'}</Typography>
                </PercentageBox>
                <StyledLinearProgress variant="determinate" value={lp.pointsScored / maxPoints * 100} />
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                    {isVr && (
                      <Tooltip title={t('myTrainings.report.getReport')}>
                        <span>
                          <ReportIconButton
                            aria-label="download-report"
                            onClick={(event) => {
                              event.stopPropagation();
                              downloadReport(lp as VrLessonProgress);
                            }}
                            disabled={isReportLoading}
                            data-testid="get-report-button"
                          >
                            <CloudDownload />
                          </ReportIconButton>
                        </span>
                      </Tooltip>
                    )}
                    <PointsBox>
                        <PointsTypography theme={theme} isCompleted={lp.lessonProgressStatus === LessonProgressStatus.Finished}>
                            {`${lp.pointsScored} / ${maxPoints}`}
                        </PointsTypography>
                    </PointsBox>
                </Box>
            </Stack>
        </StyledAccordionSummary>
        <AccordionDetails>
        {hasMounted && <Stack spacing={theme.spacing(2)}>
          {isVr && (
            <VrLessonProgressSection
              lp={lp as VrLessonProgress}
              isCompleted={lp.lessonProgressStatus === LessonProgressStatus.Finished}
            />
          )}
          {isGame && (
            <GameLessonProgressSummary
              lp={lp as GameLessonProgress}
              isCompleted={lp.lessonProgressStatus === LessonProgressStatus.Finished}
            />
          )}
          <Box padding={theme.spacing(2)}>
            <HistoricalAccordion elevation={0} disableGutters>
              <HistoricalAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="historical-progresses-content"
                  id="historical-progresses-header"
              >
                  <Typography>
                      {`${t('myTrainings.historicalProgress.archival')} (${props.historicalProgresses.length})`}
                  </Typography>
              </HistoricalAccordionSummary>
              <HistoricalProgresses historicalProgresses={props.historicalProgresses}/>
            </HistoricalAccordion>
          </Box>
        </Stack>}
        </AccordionDetails>
  </StyledAccordion>
  );
}
